import { useCallback } from "react";
import classNames from "classnames";
import Icon from "~/components/shared/Icon";
import { isFailed, State, Step } from "~/lib/pipeline";
import { StepItem } from "~/components/build/Show/pages/steps/list/components/TreeNode/StepItem";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { CollapsibleState } from "~/components/build/Show/pages/steps/list/components/TreeItem/CollapsibleState";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";

interface Props {
  step: Step;
  collapsibleState?: CollapsibleState;
  depth?: number;
  isLoading?: boolean;
  isLoaded?: boolean;
}

export function CollapsibleStepItem({
  step,
  depth = 0,
  collapsibleState = CollapsibleState.Collapsed,
  isLoading = false,
  isLoaded,
}: Props) {
  const params = useQueryParams();
  const toggleStep = useBuildStore((state) => state.toggleStep);
  const active = params.stepUuid === step.uuid;
  const isExpanded =
    collapsibleState === CollapsibleState.Expanded || collapsibleState === CollapsibleState.Full;

  const toggle = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      toggleStep(step.uuid);
    },
    [toggleStep, step.uuid],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === " ") {
        event.preventDefault();
        toggleStep(step.uuid);
      }
    },
    [toggleStep, step.uuid],
  );

  return (
    <StepItem
      step={step}
      active={active}
      depth={depth}
      isLoaded={isLoaded}
      onKeyDown={handleKeyDown}
      failed={isFailed(step) && !isExpanded}
      className={classNames({
        "animate-pulse": isLoading,
      })}
      arrow={
        <button
          type="button"
          onClick={toggle}
          tabIndex={-1}
          data-testid="collapsible-step-toggle"
          className={classNames(
            "flex h-6 w-6 cursor-pointer select-none items-center justify-center rounded-sm hover:bg-purple-100",
            {
              "opacity-45 hover:opacity-100":
                step.state === State.WaitingForDependencies && !active,
            },
          )}
        >
          <Icon
            icon="heroicons/outline/chevron-right"
            className={classNames("h-3 w-3 transition-transform duration-150", {
              "rotate-90": isExpanded,
            })}
          />
        </button>
      }
    />
  );
}
